import React, {useState, useEffect, useLayoutEffect, useRef} from 'react';
import cx from 'classnames';
import {MenuItem} from 'src/components/menu-item';
import {Link} from 'src/components/link';
import {Submenu} from 'src/components/submenu';
import {Hamburger} from 'src/components/hamburger';
import {
	useStoreContext,
	useCartCount,
	useToggleCart,
	useToggleMenu,
	useToggleSubmenu,
	useSetActiveSubmenu,
	useToggleSearch,
	useEnableNoticeBar,
	useRevealNoticeBar,
	useHideNoticeBar,
	useSetNoticeBarHeight,
} from 'src/context/storeContext';
import {useDocumentScrollThrottled} from 'src/hooks/useDocumentScrollThrottled';
import {useScrollTop} from 'src/hooks/useScrollTop';
import {useHeaderData} from 'src/hooks/useHeaderData';
import {MobileMenu} from 'src/components/mobile-menu';
import {BlockContent} from 'src/components/block-content';

import {ReactComponent as EWLogo} from 'src/images/svg/EWLogo.svg';
import {ReactComponent as ShoppingBag} from 'src/images/svg/ShoppingBag.svg';
import {ReactComponent as Search} from 'src/images/svg/Search.svg';

import * as styles from './header.module.css';

export const Header = () => {
	const {
		menuIsOpen,
		submenuIsOpen,
		cartIsOpen,
		searchIsOpen,
		activeSubmenu,
		noticeBarVisible,
	} = useStoreContext();
	const count = useCartCount();
	const toggleCart = useToggleCart();
	const toggleMenu = useToggleMenu();
	const toggleSubmenu = useToggleSubmenu();
	const setActiveSubmenu = useSetActiveSubmenu();
	const toggleSearch = useToggleSearch();
	const revealNoticeBar = useRevealNoticeBar();
	const hideNoticeBar = useHideNoticeBar();
	const setNoticeBarHeight = useSetNoticeBarHeight();
	const enableNoticeBar = useEnableNoticeBar();
	const isTopOfPage = useScrollTop();

	const {menu, mobileMenu, showNoticeBar, noticeBarCopy} = useHeaderData();

	const noticeBarRef = useRef();

	useLayoutEffect(() => {
		setNoticeBarHeight(0);
		if (showNoticeBar) {
			enableNoticeBar();
		}
		if (showNoticeBar && noticeBarRef.current) {
			setNoticeBarHeight(noticeBarRef.current.offsetHeight);
		}
	}, []);

	useEffect(() => {
		if (showNoticeBar && isTopOfPage) {
			revealNoticeBar();
		} else {
			hideNoticeBar();
		}
	}, [showNoticeBar, isTopOfPage]);

	const menuItems = menu?.items;

	const handleLogoClick = () => {
		menuIsOpen && toggleMenu();
		cartIsOpen && toggleCart();
		searchIsOpen && toggleSearch();
	};

	const handleClick = (event, item) => {
		menuIsOpen && toggleMenu();
		cartIsOpen && toggleCart();
		searchIsOpen && toggleSearch();

		switch (item.type) {
			case 'submenu':
				setActiveSubmenu(item.submenu);
				if (
					!submenuIsOpen ||
					(activeSubmenu && activeSubmenu?.id === item.submenu.id)
				) {
					toggleSubmenu();
				}
				break;

			case 'internalLink':
				submenuIsOpen && toggleSubmenu();
				break;
			default:
				return;
		}
	};

	const handleMenuToggle = () => {
		cartIsOpen && toggleCart();
		searchIsOpen && toggleSearch();

		toggleMenu();
	};

	const handleCartToggle = () => {
		menuIsOpen && toggleMenu();
		searchIsOpen && toggleSearch();

		if (submenuIsOpen) {
			setActiveSubmenu(undefined);
			toggleSubmenu();
		}

		toggleCart();
	};

	const handleSearchToggle = () => {
		menuIsOpen && toggleMenu();
		cartIsOpen && toggleCart();

		if (submenuIsOpen) {
			setActiveSubmenu(undefined);
			toggleSubmenu();
		}

		toggleSearch();
	};

	// DIY headroom functionality

	const [scrolledDown, setScrolledDown] = useState(false);
	const [headerOffscreen, setHeaderOffscreen] = useState(false);
	const MINIMUM_SCROLL = 20;

	useDocumentScrollThrottled(callbackData => {
		const {previousScrollTop, currentScrollTop} = callbackData;
		const isScrolledDown = previousScrollTop < currentScrollTop;
		const isMinimumScrolled = currentScrollTop > MINIMUM_SCROLL;
		setScrolledDown(isScrolledDown);
		setHeaderOffscreen(isScrolledDown && isMinimumScrolled);
	});

	return (
		<div className={noticeBarVisible ? styles.noticeBarVisible : ''}>
			<div
				ref={noticeBarRef}
				className={cx(
					styles.noticeBar,
					noticeBarVisible ? styles.noticeBarVisible : '',
				)}
			>
				<BlockContent
					className={cx('label-italic c-gold')}
					copy={noticeBarCopy}
				/>
			</div>
			<div
				className={cx(
					styles.headerContainer,
					headerOffscreen ? styles.offscreen : '',
					!isTopOfPage && !scrolledDown ? styles.background : '',
				)}
			>
				<Hamburger
					className={cx('hide-min-large', styles.mobileMenuToggle)}
					open={menuIsOpen}
					onClick={handleMenuToggle}
				/>
				<div className={cx('hide-min-large', styles.mobileHeaderRight)}>
					<button
						className={cx('reset', styles.mobileSearchToggle)}
						onClick={() => handleSearchToggle()}
						aria-label="Search"
					>
						<Search />
					</button>
					<button
						className={cx('reset', styles.mobileCartToggle)}
						onClick={() => handleCartToggle()}
						aria-label="Bag"
					>
						<ShoppingBag />
					</button>
				</div>

				<div className={styles.header}>
					<div className={styles.headerInner}>
						<ul
							className={cx('hide-max-medium', styles.headerMenu)}
						>
							{menuItems &&
								menuItems.map(item => {
									return (
										<MenuItem
											className={cx(
												styles.headerMenuItem,
											)}
											item={item}
											key={item._key}
											clickHandler={event =>
												handleClick(event, item)
											}
										>
											<span className="default underline-on-hover">
												{item.title}
											</span>
										</MenuItem>
									);
								})}
						</ul>
						<div className={styles.headerLogo}>
							<Link
								type="internalLink"
								to="/"
								onClick={handleLogoClick}
								ariaLabel="Erica Weiner"
							>
								<EWLogo />
							</Link>
						</div>
						<ul
							className={cx('hide-max-medium', styles.headerMenu)}
						>
							<li className={styles.headerMenuItem}>
								<button
									className={cx(
										searchIsOpen ? 'active-button' : '',
									)}
									onClick={() => handleSearchToggle()}
								>
									<span className="hide-max-medium default underline-on-hover">
										Search
									</span>
								</button>
							</li>
							<li className={styles.headerMenuItem}>
								<button
									className={cx(
										'default',
										styles.cartToggle,
										cartIsOpen ? 'active-button' : '',
									)}
									onClick={() => handleCartToggle()}
								>
									<span>
										<span className="underline-on-hover">
											Bag
										</span>
										{count > 0 && (
											<>
												<span
													className={cx(
														'italic',
														styles.cartCount,
													)}
												>
													<span
														className={
															styles.cartCountDash
														}
													>
														{'—'}
													</span>
													{count}
												</span>
											</>
										)}
									</span>
								</button>
							</li>
						</ul>
					</div>
					<Submenu menu={activeSubmenu} clickHandler={handleClick} />
					<MobileMenu data={mobileMenu} />
				</div>
			</div>
		</div>
	);
};
